import { Injectable, OnDestroy } from "@angular/core";
import {HttpClient, HttpHeaders, HttpParams, HttpResponse} from '@angular/common/http';
import { firstValueFrom, Subject, switchMap } from "rxjs";
import {environment} from 'src/environments/environment';
import {EmailValidationResponse} from "../models/email-validation-response.model";
import { filter, take, takeUntil } from "rxjs/operators";
import { NavigationEnd, Router } from "@angular/router";
import { UserInfoService } from "./user-info.service";
import { ModalService } from "./modal.service";

@Injectable({
  providedIn: 'root'
})
export class EmailValidationService implements OnDestroy{
  private destroy$ = new Subject<void>();
  private baseUrl = environment.backend.baseUrl + environment.backend.contextPath + '/rest/email-validation';

  constructor(private http: HttpClient,
              private router: Router,
              private userInfoService: UserInfoService,
              private modalService: ModalService,
              ) {}

  /**
   * Sends a validation request with the provided personal code and language,
   * along with the client's timezone offset.
   *
   * The `getTimezoneOffset` function returns the difference in minutes between
   * the local timezone and UTC. By design, this value is returned as a negative number if
   * the timezone is ahead of UTC (e.g., for Estonia, it returns -120 representing 120 minutes
   * ahead of UTC) and a positive number if the timezone is behind UTC (e.g., for New York,
   * it returns +300 representing 300 minutes behind UTC).
   *
   * To standardize this value to the typical timezone offset format,
   * the returned value is negated. For example:
   * - Estonia: `-(-120)` becomes `+120`.
   * - New York: `-(+300)` becomes `-300`.
   */
  requestValidation(personalCode: string, language: string): Promise<EmailValidationResponse | null> {

    const timezoneOffsetMinutes = -new Date().getTimezoneOffset();
    const params = new HttpParams()
      .set('personalCode', personalCode)
      .set('language', language)
      .set('timezoneOffset', timezoneOffsetMinutes.toString())

    return firstValueFrom(
      this.http.post<EmailValidationResponse>(`${this.baseUrl}/request`, params, {
        headers: new HttpHeaders({
          'Content-Type': 'application/json'
        }),
        observe: 'response',
        responseType: 'json' as const,
        withCredentials: true,
      })
    ).then((response: HttpResponse<EmailValidationResponse>) => {
      if (response.status === 200) {
        return response.body;
      } else {
        return null;
      }
    });
  }

  verifyCode(personalCode: string, code: string): Promise<EmailValidationResponse | null> {
    const params = new HttpParams()
      .set('personalCode', personalCode)
      .set('code', code);

    return firstValueFrom(
      this.http.post(`${this.baseUrl}/verify`, params, {
        headers: new HttpHeaders({
          'Content-Type': 'application/x-www-form-urlencoded'
        }),
        observe: 'response',
        responseType: 'json' as const,
        withCredentials: true,
      })
    ).then((response: HttpResponse<EmailValidationResponse>) => {
      if (response.status === 200) {
        return response.body;
      } else {
        return null;
      }
    });
  }

  subscribeToEmailValidation() {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        filter(event => event.url === '/' || event.url === '/settings/user-profile'),
        switchMap(() =>
          this.userInfoService.userInfo.pipe(
            filter(userInfo => !!userInfo?.userEmail),
            take(1)
          )
        ),
        filter(userInfo => !userInfo.isEmailValidated),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.modalService.showEmailValidationModal();
      });
  }


  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
